// ui/i18n.ts — dictionary, applier, and language picker.
//
// Extracted verbatim from an inline <script> in index.html. It still assigns
// window.I18N: client.ts reads the helper through that global and degrades
// gracefully when it is absent, and keeping the global preserves that
// contract exactly. Import order in client.ts is what now guarantees this
// runs first — the same guarantee the inline classic script used to give.

export {};

/* ---------------------------------------------------------------------------
 * I18N — dictionary, applier, and language picker.
 * Runs before client.js (modules are deferred). English lives in the markup
 * as the default and fallback; everything here swaps textContent /
 * placeholder / title on elements tagged data-i18n / data-i18n-placeholder /
 * data-i18n-title. Switching language re-applies in place — no reload, so
 * the drawn road survives.
 *
 * client.js contract:
 *   I18N.t(key)            -> translated string (falls back to en, then key)
 *   I18N.n(x, maxFrac)     -> locale-formatted number (12.4 -> "12,4" in de)
 *   I18N.locale            -> 'en' | 'de' | 'fr' | 'it' | 'es' | 'zh'
 *   'localechange' event   -> fired on document when the user switches;
 *                             re-render any strings client.js owns
 *                             (subtitle, Cars/Debug toggles, share text).
 * ------------------------------------------------------------------------ */
(window as any).I18N = (function () {
  'use strict';

  var DICT = {
    en: {
      loading_puzzle: 'Loading puzzle\u2026',
      legend_existing: 'Alternative route', legend_proposed: 'Proposed (at grade)',
      legend_bridge: 'Bridge', legend_opencut: 'Open Cut', legend_tunnel: 'Tunnel',
      legend_contour: 'Contour', legend_water: 'Watercourse',
      elevation_profile: 'Elevation Profile',
      cost: 'Cost', time: 'Time',
      proposed_route: 'Proposed Route',
      infeasible: '\u26a0 Route too short for elevation change',
      length: 'Length', travel_time: 'Travel Time',
      construction_cost: 'Cost', min_speed: 'Min Speed',
      submit_run: "Submit Today's Run", handle_ph: 'Your handle', submit_score: 'Submit Score',
      survey_title: 'Desktop Expansion Survey',
      survey_intro: 'A larger standalone game is being scoped. What should it have?',
      opt_competitors: 'Competing roads', opt_topography: 'Real-world topography',
      opt_scenarios: 'Scenarios', opt_multiplayer: 'Multiplayer', opt_regions: 'Wider regions',
      other_ph: 'Other\u2026',
      get_notified: 'Get notified on release!',
      email_ph: 'email (optional, one note at release)',
      send_answers: 'Send Answers',
      leaderboard_title: "Today's Leaderboard", plot: 'Plot',
      plot_hint: 'View as scatterplot', loading: 'Loading\u2026',
      controls: 'Controls', reset: 'Reset', recenter: 'Recenter',
      add_segment: '+ Segment', remove_segment: '\u2212 Segment',
      cars: 'Cars', on: 'ON', off: 'OFF', btn_cars: 'Cars: ON',
      debug: 'Debug', btn_debug: 'Debug: Grade',
      instructions: 'Instructions',
      instr_intro: 'Connect the two towns with a road minimizing cost and time. Get your cost to be {t}% cheaper than the previously proposed route to win today\u2019s puzzle.',
      instr_1: 'Drag green handles sticking out of the control points to bend the road',
      instr_2: 'Drag to move control points',
      instr_3: 'Click on the road to add a control point',
      instr_4: 'Right-click a control point to remove it',
      instr_5: 'Scroll to zoom, drag empty area to pan',
      instr_6: 'Double-click on profile view to zoom and pan there',
      instr_7: 'Bridges cost $15MM/km regardless of height.',
      instr_8: 'Tunnels cost $25MM/km regardless of depth.',
      instr_9: 'Cut costs scale linearly with depth until it becomes the same as a tunnel.',
      scale: 'Scale 1:50,000',
      plot_modal_title: "Today's Submissions \u00b7 Cost \u00d7 Time",
      close_esc: 'Close (Esc)',
      plegend_current: 'Your current road', plegend_sub: 'Submission',
      plegend_pareto: 'Pareto frontier', plegend_you: 'Your submission',
      err_tick: 'Tick at least one box, or write your own.',
      err_email: 'That email doesn\u2019t look right.',
      sending: 'Sending\u2026',
      thanks_email: 'Noted \u2014 thank you. I\u2019ll send just one note at release.',
      thanks: 'Noted \u2014 thank you.',
      err_rate: 'Too many tries \u2014 give it an hour.',
      err_save: 'Couldn\u2019t save that. Try again in a moment.',
      err_network: 'Network hiccup. Try again in a moment.',
      /* Control-point budget. {n} = limit, {k} = how many over. */
      cp_label: 'Control points', cp_sandbox: 'Sandbox',
      cp_sandbox_hint: 'Unlimited control points. Sandbox runs don\u2019t qualify for the leaderboard.',
      cp_no_submit: 'Sandbox run \u2014 not eligible for today\u2019s leaderboard.',
      cp_over_limit: '{k} over the limit. Remove control points to qualify.',
      instr_10: 'Leaderboard mode runs use at most {n} control points, but Sandbox mode lifts the cap.',
      music: 'Music', volume: 'Volume',
      target: 'Target',
      close: 'Close',
      par_win_title: 'Target Beaten',
      par_win_body:
      "Your road beats the alternative route on speed and has a {t}% lower cost.",
      par_status_win: "Ahead of target on cost and time",
      par_status_partial: "Ahead of target on one aspect, behind on the other",
      par_status_behind: "Behind target on both cost and time",
      /* Coach note (first-run teaching strip). */
      coach_note: 'Note',
      coach_dismiss: 'Dismiss',
      coach_prev: 'Previous note',
      coach_next: 'Next note',
      coach_drag: 'In addition to dragging the control point to move the road, drag the handles to bend it.',
      coach_structures: 'Bridges, cuts, and tunnels are automatically and optimally chosen where the topography requires them. Bridges are yellow and tunnels are black, as the legend shows. Watch what they do to the cost, profile view will help show that.',
      coach_points: 'Click and drag the road to add a control point. Right click a control point to delete it. Leaderboard runs use at most {n} control points.',
      coach_submit: 'When you like the results, submit the run.',
      coach_steep: 'Too steep to build: that stretch is too short for the height it has to climb. Give it more length.',
      hint_fall_line_snap: "Press F to set the road's direction at the last control point you touched perpendicular to the fall line. Hold Shift while dragging a handle to keep its angle and change only its length.",
    },

    /* Swiss Standard German: ss, never \u00df. */
    de: {
      loading_puzzle: 'R\u00e4tsel wird geladen\u2026',
      legend_existing: 'Alternativroute', legend_proposed: 'Geplant (ebenerdig)',
      legend_bridge: 'Br\u00fccke', legend_opencut: 'Einschnitt', legend_tunnel: 'Tunnel',
      legend_contour: 'H\u00f6henlinie', legend_water: 'Gew\u00e4sser',
      elevation_profile: 'H\u00f6henprofil',
      cost: 'Kosten', time: 'Zeit',
      proposed_route: 'Geplante Route',
      infeasible: '\u26a0 Route zu kurz f\u00fcr den H\u00f6henunterschied',
      length: 'L\u00e4nge', travel_time: 'Fahrzeit',
      construction_cost: 'Baukosten', min_speed: 'Min. Tempo',
      submit_run: 'Heutigen Lauf einreichen', handle_ph: 'Dein Name', submit_score: 'Score einreichen',
      survey_title: 'Umfrage: Desktop-Version',
      survey_intro: 'Ein gr\u00f6sseres eigenst\u00e4ndiges Spiel ist in Planung. Was soll es k\u00f6nnen?',
      opt_competitors: 'Konkurrierende Strassen', opt_topography: 'Echte Topografie',
      opt_scenarios: 'Szenarien', opt_multiplayer: 'Mehrspieler', opt_regions: 'Gr\u00f6ssere Regionen',
      other_ph: 'Anderes\u2026',
      get_notified: 'Lass dich benachrichtigen, wenn das Spiel startet',
      email_ph: 'email (optional, eine Nachricht zum Release)',
      send_answers: 'Antworten senden',
      leaderboard_title: 'Heutige Rangliste', plot: 'Grafik',
      plot_hint: 'Als Streudiagramm anzeigen', loading: 'Laden\u2026',
      controls: 'Steuerung', reset: 'Zur\u00fccksetzen', recenter: 'Zentrieren',
      add_segment: '+ Segment', remove_segment: '\u2212 Segment',
      cars: 'Autos', on: 'AN', off: 'AUS', btn_cars: 'Autos: AN',
      debug: 'Debug', btn_debug: 'Debug: Steigung',
      instructions: 'Anleitung',
      instr_intro: 'Verbinde die beiden Orte mit einer Strasse \u2014 m\u00f6glichst g\u00fcnstig und schnell. Um das heutige R\u00e4tsel zu gewinnen, muss deine Strasse {t}\u00a0% g\u00fcnstiger sein als die zuvor vorgeschlagene Route.',
      instr_1: 'Die grünen Griffe an den Kontrollpunkten ziehen, um die Strasse zu biegen',
      instr_2: 'Kontrollpunkte ziehen, um sie zu verschieben',
      instr_3: 'Auf die Strasse klicken, um einen Kontrollpunkt hinzuzufügen',
      instr_4: 'Rechtsklick auf einen Kontrollpunkt, um ihn zu entfernen',
      instr_5: 'Scrollen zum Zoomen \u00b7 leere Fl\u00e4che ziehen zum Verschieben',
      instr_6: 'Doppelklick aufs Höhenprofil, um dort zu zoomen und zu verschieben',
      instr_7: 'Brücken kosten 15 Mio. $/km, unabhängig von der Höhe.',
      instr_8: 'Tunnel kosten 25 Mio. $/km, unabhängig von der Tiefe.',
      instr_9: 'Die Kosten für Einschnitte steigen linear mit der Tiefe, bis sie denen eines Tunnels entsprechen.',
      scale: "Massstab 1:50'000",
      plot_modal_title: 'Heutige Einreichungen \u00b7 Kosten \u00d7 Zeit',
      close_esc: 'Schliessen (Esc)',
      plegend_current: 'Deine aktuelle Strasse', plegend_sub: 'Einreichung',
      plegend_pareto: 'Pareto-Front', plegend_you: 'Deine Einreichung',
      err_tick: 'Mindestens ein Feld ankreuzen oder selbst etwas schreiben.',
      err_email: 'Diese E-Mail sieht nicht richtig aus.',
      sending: 'Wird gesendet\u2026',
      thanks_email: 'Notiert \u2014 danke. Zum Release gibt\u2019s genau eine Nachricht.',
      thanks: 'Notiert \u2014 danke.',
      err_rate: 'Zu viele Versuche \u2014 probier\u2019s in einer Stunde wieder.',
      err_save: 'Konnte nicht gespeichert werden. Gleich nochmals versuchen.',
      err_network: 'Netzwerkproblem. Gleich nochmals versuchen.',
      cp_label: 'Kontrollpunkte', cp_sandbox: 'Sandbox',
      cp_sandbox_hint: 'Unbegrenzte Kontrollpunkte. Sandbox-L\u00e4ufe z\u00e4hlen nicht f\u00fcr die Rangliste.',
      cp_no_submit: 'Sandbox-Lauf \u2014 nicht f\u00fcr die heutige Rangliste zugelassen.',
      cp_over_limit: '{k} \u00fcber dem Limit. Entferne Kontrollpunkte, damit der Lauf zugelassen wird.',
      instr_10: 'F\u00fcr die Rangliste sind h\u00f6chstens {n} Kontrollpunkte erlaubt \u2014 Sandbox hebt das Limit auf.',
      music: 'Musik', volume: 'Lautst\u00e4rke',
      target: 'Ziel',
      close: 'Schliessen',
      par_win_title: 'Ziel übertroffen',
      par_win_body: "Deine Strasse ist schneller als die Alternativroute und verursacht {t}\u00a0% geringere Kosten.",
      par_status_win: "Bei Kosten und Zeit besser als das Ziel",
      par_status_partial: "In einem Punkt besser als das Ziel, im anderen schlechter",
      par_status_behind: "Bei Kosten und Zeit schlechter als das Ziel",
      /* Coach note (first-run teaching strip). */
      coach_note: 'Hinweis',
      coach_dismiss: 'Ausblenden',
      coach_prev: 'Vorheriger Hinweis',
      coach_next: 'Nächster Hinweis',
      coach_drag: 'Ziehe nicht nur den Kontrollpunkt, um die Strasse zu verschieben — zieh auch die Griffe, um sie zu biegen.',
      coach_structures: 'Brücken, Einschnitte und Tunnel werden dort automatisch und optimal gewählt, wo das Gelände es verlangt. Brücken sind gelb, Tunnel schwarz, wie in der Legende. Achte darauf, was sie mit den Kosten machen; das Höhenprofil hilft dabei.',
      coach_points: 'Klicke auf die Strasse und ziehe, um einen Kontrollpunkt hinzuzufügen. Mit einem Rechtsklick auf einen Kontrollpunkt löschst du ihn. Für die Rangliste sind höchstens {n} Kontrollpunkte erlaubt.',
      coach_submit: 'Wenn dir die Ergebnisse gefallen, reiche den Lauf ein.',
      coach_steep: 'Zu steil zum Bauen: Dieser Abschnitt ist zu kurz für den Höhenunterschied. Gib ihm mehr Länge.',
      hint_fall_line_snap: "Dr\u00fccke F, um die Strassenrichtung am zuletzt bearbeiteten Kontrollpunkt senkrecht zur Falllinie auszurichten. Halte Umschalt gedr\u00fcckt, w\u00e4hrend du einen Griff ziehst, um dessen Winkel beizubehalten und nur die L\u00e4nge zu \u00e4ndern."
    },

    fr: {
      loading_puzzle: 'Chargement du puzzle\u2026',
      legend_existing: 'Itinéraire alternatif', legend_proposed: 'Propos\u00e9e (\u00e0 niveau)',
      legend_bridge: 'Pont', legend_opencut: 'Tranch\u00e9e', legend_tunnel: 'Tunnel',
      legend_contour: 'Courbe de niveau', legend_water: "Cours d'eau",
      elevation_profile: 'Profil altim\u00e9trique',
      cost: 'Co\u00fbt', time: 'Temps',
      proposed_route: 'Trac\u00e9 propos\u00e9',
      infeasible: '\u26a0 Trac\u00e9 trop court pour le d\u00e9nivel\u00e9',
      length: 'Longueur', travel_time: 'Temps de trajet',
      construction_cost: 'Co\u00fbt', min_speed: 'Vitesse min.',
      submit_run: 'Soumettre le trac\u00e9 du jour', handle_ph: 'Votre pseudo', submit_score: 'Soumettre le score',
      survey_title: 'Sondage : version compl\u00e8te',
      survey_intro: 'Un jeu complet est \u00e0 l\u2019\u00e9tude. Que devrait-il proposer ?',
      opt_competitors: 'Routes concurrentes', opt_topography: 'Topographie r\u00e9elle',
      opt_scenarios: 'Sc\u00e9narios', opt_multiplayer: 'Multijoueur', opt_regions: 'R\u00e9gions plus vastes',
      other_ph: 'Autre\u2026',
      get_notified: 'Soyez averti lors du lancement du jeu',
      email_ph: 'email (facultatif, un seul message \u00e0 la sortie)',
      send_answers: 'Envoyer les r\u00e9ponses',
      leaderboard_title: 'Classement du jour', plot: 'Graphe',
      plot_hint: 'Afficher en nuage de points', loading: 'Chargement\u2026',
      controls: 'Commandes', reset: 'R\u00e9initialiser', recenter: 'Recentrer',
      add_segment: '+ Segment', remove_segment: '\u2212 Segment',
      cars: 'Voitures', on: 'ON', off: 'OFF', btn_cars: 'Voitures : ON',
      debug: 'Debug', btn_debug: 'Debug : Pente',
      instructions: 'Instructions',
      instr_intro: 'Reliez les deux villes par une route en minimisant co\u00fbt et temps. Pour gagner l\u2019\u00e9nigme du jour, votre route doit co\u00fbter {t}\u202f% de moins que l\u2019itin\u00e9raire propos\u00e9 pr\u00e9c\u00e9demment.',
      instr_1: 'Glissez les poignées vertes qui sortent des points de contrôle pour courber la route',
      instr_2: 'Glissez les points de contrôle pour les déplacer',
      instr_3: 'Cliquez sur la route pour ajouter un point de contrôle',
      instr_4: 'Clic droit sur un point de contrôle pour le supprimer',
      instr_5: 'Molette pour zoomer \u00b7 glissez le fond pour d\u00e9placer',
      instr_6: 'Double-clic sur le profil pour y zoomer et naviguer',
      instr_7: 'Les ponts coûtent 15 M$/km, quelle que soit leur hauteur.',
      instr_8: 'Les tunnels coûtent 25 M$/km, quelle que soit leur profondeur.',
      instr_9: 'Le coût des tranchées augmente linéairement avec la profondeur, jusqu\u2019à égaler celui d\u2019un tunnel.',
      scale: '\u00c9chelle 1:50 000',
      plot_modal_title: 'Soumissions du jour \u00b7 Co\u00fbt \u00d7 Temps',
      close_esc: 'Fermer (\u00c9chap)',
      plegend_current: 'Votre route actuelle', plegend_sub: 'Soumission',
      plegend_pareto: 'Front de Pareto', plegend_you: 'Votre soumission',
      err_tick: 'Cochez au moins une case, ou \u00e9crivez la v\u00f4tre.',
      err_email: 'Cette adresse e-mail semble invalide.',
      sending: 'Envoi\u2026',
      thanks_email: 'C\u2019est not\u00e9 \u2014 merci. Un seul message \u00e0 la sortie.',
      thanks: 'C\u2019est not\u00e9 \u2014 merci.',
      err_rate: 'Trop d\u2019essais \u2014 r\u00e9essayez dans une heure.',
      err_save: '\u00c9chec de l\u2019enregistrement. R\u00e9essayez dans un instant.',
      err_network: 'Probl\u00e8me r\u00e9seau. R\u00e9essayez dans un instant.',
      cp_label: 'Points de contr\u00f4le', cp_sandbox: 'Bac \u00e0 sable',
      cp_sandbox_hint: 'Points de contr\u00f4le illimit\u00e9s. Les trac\u00e9s en bac \u00e0 sable ne comptent pas pour le classement.',
      cp_no_submit: 'Trac\u00e9 en bac \u00e0 sable \u2014 non admissible au classement du jour.',
      cp_over_limit: '{k} de trop. Retirez des points de contr\u00f4le pour \u00eatre admissible.',
      instr_10: 'Le classement accepte au plus {n} points de contr\u00f4le \u2014 le bac \u00e0 sable l\u00e8ve la limite.',
      music: 'Musique', volume: 'Volume',
      target: 'Objectif',
      close: 'Fermer',
      par_win_title: 'Objectif battu',

      par_win_body: "Votre route est plus rapide que l\u2019itin\u00e9raire alternatif et co\u00fbte {t}\u202f% de moins.",
      par_status_win: "Meilleur que l\u2019objectif en co\u00fbt et en temps",
      par_status_partial: "Meilleur que l\u2019objectif sur un crit\u00e8re, moins bon sur l\u2019autre",
      par_status_behind: "Moins bon que l\u2019objectif en co\u00fbt et en temps",
      /* Coach note (first-run teaching strip). */
      coach_note: 'Note',
      coach_dismiss: 'Masquer',
      coach_prev: 'Note précédente',
      coach_next: 'Note suivante',
      coach_drag: 'En plus de faire glisser le point de contrôle pour déplacer la route, faites glisser les poignées pour la courber.',
      coach_structures: 'Ponts, tranchées et tunnels sont choisis automatiquement et de façon optimale là où le relief l’exige. Les ponts sont jaunes et les tunnels noirs, comme l’indique la légende. Observez leur effet sur le coût ; le profil altimétrique aide à le voir.',
      coach_points: 'Cliquez sur la route et faites glisser pour ajouter un point de contrôle. Faites un clic droit sur un point de contrôle pour le supprimer. Le classement autorise au maximum {n} points de contrôle.',
      coach_submit: 'Quand les résultats vous conviennent, soumettez votre tracé.',
      coach_steep: 'Trop raide à construire : ce tronçon est trop court pour le dénivelé à franchir. Allongez-le.',
      hint_fall_line_snap: "Appuyez sur F pour orienter la route au dernier point de contr\u00f4le modifi\u00e9 perpendiculairement \u00e0 la ligne de plus grande pente. Maintenez Maj enfonc\u00e9e en d\u00e9pla\u00e7ant une poign\u00e9e pour conserver son angle et n\u2019en modifier que la longueur."
    },

    it: {
      loading_puzzle: 'Caricamento del puzzle\u2026',
      legend_existing: 'Percorso alternativo', legend_proposed: 'Proposta (a raso)',
      legend_bridge: 'Ponte', legend_opencut: 'Trincea', legend_tunnel: 'Galleria',
      legend_contour: 'Curva di livello', legend_water: "Corso d'acqua",
      elevation_profile: 'Profilo altimetrico',
      cost: 'Costo', time: 'Tempo',
      proposed_route: 'Tracciato proposto',
      infeasible: '\u26a0 Tracciato troppo corto per il dislivello',
      length: 'Lunghezza', travel_time: 'Tempo di percorrenza',
      construction_cost: 'Costo', min_speed: 'Velocit\u00e0 min.',
      submit_run: 'Invia il tracciato di oggi', handle_ph: 'Il tuo nome', submit_score: 'Invia punteggio',
      survey_title: 'Sondaggio: versione completa',
      survey_intro: '\u00c8 in valutazione un gioco completo. Cosa dovrebbe avere?',
      opt_competitors: 'Strade concorrenti', opt_topography: 'Topografia reale',
      opt_scenarios: 'Scenari', opt_multiplayer: 'Multigiocatore', opt_regions: 'Regioni pi\u00f9 ampie',
      other_ph: 'Altro\u2026',
      get_notified: 'Ricevi una notifica quando il gioco viene avviato.',
      email_ph: 'email (facoltativo, un solo avviso all\u2019uscita)',
      send_answers: 'Invia risposte',
      leaderboard_title: 'Classifica di oggi', plot: 'Grafico',
      plot_hint: 'Mostra come grafico a dispersione', loading: 'Caricamento\u2026',
      controls: 'Comandi', reset: 'Reimposta', recenter: 'Centra',
      add_segment: '+ Segmento', remove_segment: '\u2212 Segmento',
      cars: 'Auto', on: 'ON', off: 'OFF', btn_cars: 'Auto: ON',
      debug: 'Debug', btn_debug: 'Debug: Pendenza',
      instructions: 'Istruzioni',
      instr_intro: 'Collega le due citt\u00e0 con una strada minimizzando costo e tempo. Per vincere il puzzle di oggi, la tua strada deve costare il {t}% in meno del percorso proposto in precedenza.',
      instr_1: 'Trascina le maniglie verdi che sporgono dai punti di controllo per curvare la strada',
      instr_2: 'Trascina i punti di controllo per spostarli',
      instr_3: 'Clicca sulla strada per aggiungere un punto di controllo',
      instr_4: 'Clic destro su un punto di controllo per rimuoverlo',
      instr_5: 'Rotella per lo zoom \u00b7 trascina lo sfondo per spostarti',
      instr_6: 'Doppio clic sul profilo per zoomare e navigare l\u00ec',
      instr_7: 'I ponti costano 15 mln $/km, indipendentemente dall\u2019altezza.',
      instr_8: 'Le gallerie costano 25 mln $/km, indipendentemente dalla profondità.',
      instr_9: 'Il costo delle trincee cresce linearmente con la profondità, fino a eguagliare quello di una galleria.',
      scale: 'Scala 1:50 000',
      plot_modal_title: 'Invii di oggi \u00b7 Costo \u00d7 Tempo',
      close_esc: 'Chiudi (Esc)',
      plegend_current: 'La tua strada attuale', plegend_sub: 'Invio',
      plegend_pareto: 'Frontiera di Pareto', plegend_you: 'Il tuo invio',
      err_tick: 'Spunta almeno una casella o scrivi la tua.',
      err_email: 'Questa e-mail non sembra valida.',
      sending: 'Invio\u2026',
      thanks_email: 'Registrato \u2014 grazie. Un solo avviso all\u2019uscita.',
      thanks: 'Registrato \u2014 grazie.',
      err_rate: 'Troppi tentativi \u2014 riprova tra un\u2019ora.',
      err_save: 'Salvataggio non riuscito. Riprova tra poco.',
      err_network: 'Problema di rete. Riprova tra poco.',
      cp_label: 'Punti di controllo', cp_sandbox: 'Sandbox',
      cp_sandbox_hint: 'Punti di controllo illimitati. I tracciati in sandbox non valgono per la classifica.',
      cp_no_submit: 'Tracciato in sandbox \u2014 non valido per la classifica di oggi.',
      cp_over_limit: '{k} oltre il limite. Rimuovi punti di controllo per qualificarti.',
      instr_10: 'La classifica ammette al massimo {n} punti di controllo \u2014 la sandbox toglie il limite.',
      music: 'Musica', volume: 'Volume',
      target: 'Obiettivo',
      close: 'Chiudi',
      par_win_title: 'Obiettivo superato',
      par_win_body: "La tua strada \u00e8 pi\u00f9 veloce del percorso alternativo e costa il {t}% in meno.",
      par_status_win: "Meglio dell\u2019obiettivo per costo e tempo",
      par_status_partial: "Meglio dell\u2019obiettivo per un aspetto, peggio per l\u2019altro",
      par_status_behind: "Peggio dell\u2019obiettivo sia per costo sia per tempo",
      /* Coach note (first-run teaching strip). */
      coach_note: 'Nota',
      coach_dismiss: 'Nascondi',
      coach_prev: 'Nota precedente',
      coach_next: 'Nota successiva',
      coach_drag: 'Oltre a trascinare il punto di controllo per spostare la strada, trascina le maniglie per curvarla.',
      coach_structures: 'Ponti, trincee e gallerie vengono scelti automaticamente e in modo ottimale dove il rilievo lo richiede. I ponti sono gialli e le gallerie nere, come mostra la legenda. Osserva il loro effetto sul costo; il profilo altimetrico aiuta a vederlo.',
      coach_points: 'Clicca sulla strada e trascina per aggiungere un punto di controllo. Fai clic destro su un punto di controllo per eliminarlo. In classifica si possono usare al massimo {n} punti di controllo.',
      coach_submit: 'Quando i risultati ti convincono, invia il tracciato.',
      coach_steep: 'Troppo ripida da costruire: quel tratto è troppo corto per il dislivello da superare. Allungalo.',
      hint_fall_line_snap: "Premi F per orientare la strada nell\u2019ultimo punto di controllo modificato perpendicolarmente alla linea di massima pendenza. Tieni premuto Maiusc mentre trascini una maniglia per mantenerne l\u2019angolo e cambiarne solo la lunghezza."
    },

    es: {
      loading_puzzle: 'Cargando el puzle\u2026',
      legend_existing: 'Ruta alternativa', legend_proposed: 'Propuesta (a nivel)',
      legend_bridge: 'Puente', legend_opencut: 'Trinchera', legend_tunnel: 'T\u00fanel',
      legend_contour: 'Curva de nivel', legend_water: 'Cauce',
      elevation_profile: 'Perfil de elevaci\u00f3n',
      cost: 'Coste', time: 'Tiempo',
      proposed_route: 'Trazado propuesto',
      infeasible: '\u26a0 Trazado demasiado corto para el desnivel',
      length: 'Longitud', travel_time: 'Tiempo de viaje',
      construction_cost: 'Coste', min_speed: 'Velocidad m\u00edn.',
      submit_run: 'Enviar el trazado de hoy', handle_ph: 'Tu apodo', submit_score: 'Enviar puntuaci\u00f3n',
      survey_title: 'Encuesta: versi\u00f3n completa',
      survey_intro: 'Se est\u00e1 estudiando un juego completo. \u00bfQu\u00e9 deber\u00eda tener?',
      opt_competitors: 'Carreteras rivales', opt_topography: 'Topograf\u00eda real',
      opt_scenarios: 'Escenarios', opt_multiplayer: 'Multijugador', opt_regions: 'Regiones m\u00e1s amplias',
      other_ph: 'Otro\u2026',
      get_notified: 'Recibe notificaciones cuando se lance',
      email_ph: 'email (opcional, un solo aviso al lanzamiento)',
      send_answers: 'Enviar respuestas',
      leaderboard_title: 'Clasificaci\u00f3n de hoy', plot: 'Gr\u00e1fico',
      plot_hint: 'Ver como diagrama de dispersi\u00f3n', loading: 'Cargando\u2026',
      controls: 'Controles', reset: 'Restablecer', recenter: 'Centrar',
      add_segment: '+ Segmento', remove_segment: '\u2212 Segmento',
      cars: 'Coches', on: 'ON', off: 'OFF', btn_cars: 'Coches: ON',
      debug: 'Debug', btn_debug: 'Debug: Pendiente',
      instructions: 'Instrucciones',
      instr_intro: 'Conecta las dos ciudades con una carretera minimizando coste y tiempo. Para ganar el reto de hoy, tu carretera debe costar un {t}\u00a0% menos que la ruta propuesta anteriormente.',
      instr_1: 'Arrastra los tiradores verdes que salen de los puntos de control para curvar la carretera',
      instr_2: 'Arrastra los puntos de control para moverlos',
      instr_3: 'Haz clic en la carretera para añadir un punto de control',
      instr_4: 'Clic derecho en un punto de control para eliminarlo',
      instr_5: 'Rueda para hacer zoom \u00b7 arrastra el fondo para desplazarte',
      instr_6: 'Doble clic en el perfil para hacer zoom y desplazarte all\u00ed',
      instr_7: 'Los puentes cuestan 15 M$/km, independientemente de la altura.',
      instr_8: 'Los túneles cuestan 25 M$/km, independientemente de la profundidad.',
      instr_9: 'El coste de las trincheras aumenta linealmente con la profundidad, hasta igualar el de un túnel.',
      scale: 'Escala 1:50 000',
      plot_modal_title: 'Env\u00edos de hoy \u00b7 Coste \u00d7 Tiempo',
      close_esc: 'Cerrar (Esc)',
      plegend_current: 'Tu carretera actual', plegend_sub: 'Env\u00edo',
      plegend_pareto: 'Frontera de Pareto', plegend_you: 'Tu env\u00edo',
      err_tick: 'Marca al menos una casilla o escribe la tuya.',
      err_email: 'Ese correo no parece v\u00e1lido.',
      sending: 'Enviando\u2026',
      thanks_email: 'Anotado \u2014 gracias. Un solo aviso al lanzamiento.',
      thanks: 'Anotado \u2014 gracias.',
      err_rate: 'Demasiados intentos \u2014 prueba en una hora.',
      err_save: 'No se pudo guardar. Int\u00e9ntalo de nuevo en un momento.',
      err_network: 'Problema de red. Int\u00e9ntalo de nuevo en un momento.',
      cp_label: 'Puntos de control', cp_sandbox: 'Sandbox',
      cp_sandbox_hint: 'Puntos de control ilimitados. Los trazados en sandbox no cuentan para la clasificaci\u00f3n.',
      cp_no_submit: 'Trazado en sandbox \u2014 no v\u00e1lido para la clasificaci\u00f3n de hoy.',
      cp_over_limit: '{k} por encima del l\u00edmite. Quita puntos de control para poder clasificar.',
      instr_10: 'La clasificaci\u00f3n admite como m\u00e1ximo {n} puntos de control \u2014 el sandbox quita el l\u00edmite.',
      music: 'M\u00fasica', volume: 'Volumen',
      target: 'Objetivo',
      close: 'Cerrar',
      par_win_title: 'Objetivo superado',
      par_win_body: "Tu carretera es m\u00e1s r\u00e1pida que la ruta alternativa y cuesta un {t}\u00a0% menos.",
      par_status_win: "Mejor que el objetivo en coste y tiempo",
      par_status_partial: "Mejor que el objetivo en un aspecto y peor en el otro",
      par_status_behind: "Peor que el objetivo en coste y tiempo",
      /* Coach note (first-run teaching strip). */
      coach_note: 'Nota',
      coach_dismiss: 'Ocultar',
      coach_prev: 'Nota anterior',
      coach_next: 'Nota siguiente',
      coach_drag: 'Además de arrastrar el punto de control para mover la carretera, arrastra los manejadores para curvarla.',
      coach_structures: 'Los puentes, trincheras y túneles se eligen automática y óptimamente donde el relieve lo exige. Los puentes son amarillos y los túneles negros, como indica la leyenda. Fíjate en lo que le hacen al coste; el perfil de elevación ayuda a verlo.',
      coach_points: 'Haz clic en la carretera y arrastra para añadir un punto de control. Haz clic derecho en un punto de control para eliminarlo. En la clasificación se permiten como máximo {n} puntos de control.',
      coach_submit: 'Cuando te gusten los resultados, envía tu trazado.',
      coach_steep: 'Demasiado empinada para construirla: ese tramo es muy corto para el desnivel que debe salvar. Alárgalo.',
      hint_fall_line_snap: "Pulsa F para orientar la carretera en el \u00faltimo punto de control modificado perpendicularmente a la l\u00ednea de m\u00e1xima pendiente. Mant\u00e9n pulsada May\u00fas mientras arrastras un manejador para conservar su \u00e1ngulo y cambiar solo su longitud."
    },

    /* Simplified Chinese. */
    zh: {
      loading_puzzle: '\u8c1c\u9898\u52a0\u8f7d\u4e2d\u2026',
      legend_existing: '备选路线', legend_proposed: '\u62df\u5efa\uff08\u5e73\u9762\uff09',
      legend_bridge: '\u6865\u6881', legend_opencut: '\u8def\u5811', legend_tunnel: '\u96a7\u9053',
      legend_contour: '\u7b49\u9ad8\u7ebf', legend_water: '\u6c34\u7cfb',
      elevation_profile: '\u9ad8\u7a0b\u5256\u9762',
      cost: '\u9020\u4ef7', time: '\u65f6\u95f4',
      proposed_route: '\u62df\u5efa\u8def\u7ebf',
      infeasible: '\u26a0 \u8def\u7ebf\u592a\u77ed\uff0c\u65e0\u6cd5\u5b8c\u6210\u9ad8\u5dee',
      length: '\u957f\u5ea6', travel_time: '\u884c\u8f66\u65f6\u95f4',
      construction_cost: '\u5efa\u8bbe\u6210\u672c', min_speed: '\u6700\u4f4e\u8f66\u901f',
      submit_run: '\u63d0\u4ea4\u4eca\u65e5\u8def\u7ebf', handle_ph: '\u4f60\u7684\u6635\u79f0', submit_score: '\u63d0\u4ea4\u6210\u7ee9',
      survey_title: '\u5b8c\u6574\u7248\u8c03\u67e5',
      survey_intro: '\u5b8c\u6574\u7248\u6e38\u620f\u6b63\u5728\u89c4\u5212\u4e2d\u3002\u5b83\u5e94\u8be5\u6709\u4ec0\u4e48\uff1f',
      opt_competitors: '\u7ade\u4e89\u9053\u8def', opt_topography: '\u771f\u5b9e\u5730\u5f62',
      opt_scenarios: '\u573a\u666f\u6a21\u5f0f', opt_multiplayer: '\u591a\u4eba\u6e38\u620f', opt_regions: '\u66f4\u5927\u533a\u57df',
      other_ph: '\u5176\u4ed6\u2026',
      get_notified: '游戏启动时收到通知',
      email_ph: 'you@example.com \u2014\u2014 \u53ef\u9009\uff0c\u53d1\u5e03\u65f6\u4ec5\u901a\u77e5\u4e00\u6b21',
      send_answers: '\u53d1\u9001\u7b54\u6848',
      leaderboard_title: '\u4eca\u65e5\u6392\u884c\u699c', plot: '\u6563\u70b9\u56fe',
      plot_hint: '\u4ee5\u6563\u70b9\u56fe\u67e5\u770b', loading: '\u52a0\u8f7d\u4e2d\u2026',
      controls: '\u64cd\u4f5c', reset: '\u91cd\u7f6e', recenter: '\u56de\u5230\u4e2d\u5fc3',
      add_segment: '+ \u8def\u6bb5', remove_segment: '\u2212 \u8def\u6bb5',
      cars: '\u8f66\u8f86', on: '\u5f00', off: '\u5173', btn_cars: '\u8f66\u8f86\uff1a\u5f00',
      debug: '\u8c03\u8bd5', btn_debug: '\u8c03\u8bd5\uff1a\u5761\u5ea6',
      instructions: '\u73a9\u6cd5\u8bf4\u660e',
      instr_intro: '用一条道路连接两座城镇，尽量降低造价和时间。要赢得今天的谜题，你的造价需比先前提出的路线低 {t}%。',
      instr_1: '拖动控制点上伸出的绿色手柄，弯曲道路',
      instr_2: '拖动控制点即可移动',
      instr_3: '点击道路添加控制点',
      instr_4: '右键点击控制点将其删除',
      instr_5: '\u6eda\u8f6e\u7f29\u653e \u00b7 \u62d6\u52a8\u7a7a\u767d\u5904\u5e73\u79fb',
      instr_6: '\u53cc\u51fb\u5256\u9762\u56fe\u53ef\u5728\u5176\u4e2d\u7f29\u653e\u548c\u5e73\u79fb',
      instr_7: '桥梁造价为每公里 1500 万美元，与高度无关。',
      instr_8: '隧道造价为每公里 2500 万美元，与深度无关。',
      instr_9: '路堑造价随深度线性增长，直至与隧道造价相同。',
      scale: '\u6bd4\u4f8b\u5c3a 1:50,000',
      plot_modal_title: '\u4eca\u65e5\u63d0\u4ea4 \u00b7 \u9020\u4ef7 \u00d7 \u65f6\u95f4',
      close_esc: '\u5173\u95ed\uff08Esc\uff09',
      plegend_current: '\u4f60\u5f53\u524d\u7684\u9053\u8def', plegend_sub: '\u63d0\u4ea4\u8bb0\u5f55',
      plegend_pareto: '\u5e15\u7d2f\u6258\u524d\u6cbf', plegend_you: '\u4f60\u7684\u63d0\u4ea4',
      err_tick: '\u8bf7\u81f3\u5c11\u52fe\u9009\u4e00\u9879\uff0c\u6216\u81ea\u884c\u586b\u5199\u3002',
      err_email: '\u90ae\u7bb1\u683c\u5f0f\u4f3c\u4e4e\u4e0d\u5bf9\u3002',
      sending: '\u53d1\u9001\u4e2d\u2026',
      thanks_email: '\u5df2\u8bb0\u5f55\u2014\u2014\u8c22\u8c22\u3002\u53d1\u5e03\u65f6\u53ea\u53d1\u4e00\u5c01\u901a\u77e5\u3002',
      thanks: '\u5df2\u8bb0\u5f55\u2014\u2014\u8c22\u8c22\u3002',
      err_rate: '\u5c1d\u8bd5\u6b21\u6570\u8fc7\u591a\u2014\u2014\u4e00\u5c0f\u65f6\u540e\u518d\u8bd5\u3002',
      err_save: '\u4fdd\u5b58\u5931\u8d25\u3002\u8bf7\u7a0d\u540e\u518d\u8bd5\u3002',
      err_network: '\u7f51\u7edc\u51fa\u4e86\u70b9\u95ee\u9898\u3002\u8bf7\u7a0d\u540e\u518d\u8bd5\u3002',
      cp_label: '\u63a7\u5236\u70b9', cp_sandbox: '\u6c99\u76d2',
      cp_sandbox_hint: '\u63a7\u5236\u70b9\u4e0d\u53d7\u9650\u5236\u3002\u6c99\u76d2\u6a21\u5f0f\u7684\u6210\u7ee9\u4e0d\u8ba1\u5165\u6392\u884c\u699c\u3002',
      cp_no_submit: '\u6c99\u76d2\u6a21\u5f0f\u2014\u2014\u4e0d\u8ba1\u5165\u4eca\u65e5\u6392\u884c\u699c\u3002',
      cp_over_limit: '\u8d85\u51fa {k} \u4e2a\u3002\u5220\u9664\u63a7\u5236\u70b9\u540e\u65b9\u53ef\u53c2\u4e0e\u6392\u540d\u3002',
      instr_10: '\u6392\u884c\u699c\u6210\u7ee9\u6700\u591a\u4f7f\u7528 {n} \u4e2a\u63a7\u5236\u70b9\u2014\u2014\u6c99\u76d2\u6a21\u5f0f\u53d6\u6d88\u6b64\u9650\u5236\u3002',
      music: '\u97f3\u4e50', volume: '\u97f3\u91cf',
      target: '目标',
      close: '关闭',
      par_win_title: '已超越目标',
      par_win_body: "你修建的道路比备选路线更快，成本也降低了 {t}%。",
      par_status_win: "\u6210\u672c\u548c\u65f6\u95f4\u5747\u4f18\u4e8e\u76ee\u6807",
      par_status_partial: "\u4e00\u9879\u4f18\u4e8e\u76ee\u6807\uff0c\u53e6\u4e00\u9879\u672a\u8fbe\u6807",
      par_status_behind: "\u6210\u672c\u548c\u65f6\u95f4\u5747\u672a\u8fbe\u76ee\u6807",
      /* Coach note (first-run teaching strip). */
      coach_note: '提示',
      coach_dismiss: '隐藏',
      coach_prev: '上一条提示',
      coach_next: '下一条提示',
      coach_drag: '除了拖动控制点来移动道路，还可以拖动控制柄来调整弯道。',
      coach_structures: '在地形需要的位置，会自动选择最优的桥梁、路堑或隧道方案。如图例所示，桥梁为黄色，隧道为黑色。留意它们对造价的影响——高程剖面能帮你看清这一点。',
      coach_points: '在道路上点击并拖动即可添加控制点，右键点击控制点可将其删除。排行榜成绩最多可用 {n} 个控制点。',
      coach_submit: '对结果满意后，提交你的路线。',
      coach_steep: '坡度过陡，无法修建：该路段太短，爬升不了这个高差。请加长。',
      hint_fall_line_snap: "\u6309 F \u952e\u53ef\u5c06\u6700\u540e\u4fee\u6539\u7684\u63a7\u5236\u70b9\u5904\u7684\u9053\u8def\u65b9\u5411\u8c03\u6574\u4e3a\u4e0e\u6700\u5927\u5761\u5ea6\u7ebf\u5782\u76f4\u3002\u62d6\u52a8\u63a7\u5236\u67c4\u65f6\u6309\u4f4f Shift \u952e\uff0c\u53ef\u4fdd\u6301\u5176\u89d2\u5ea6\u4e0d\u53d8\uff0c\u4ec5\u6539\u53d8\u957f\u5ea6\u3002"
    },

    jp: {
      loading_puzzle: 'パズルを読み込み中\u2026',
      legend_existing: '代替ルート', legend_proposed: '計画道路（平面）',
      legend_bridge: '橋梁', legend_opencut: '開削', legend_tunnel: 'トンネル',
      legend_contour: '等高線', legend_water: '河川',
      elevation_profile: '縦断図',
      cost: 'コスト', time: '時間',
      proposed_route: '計画ルート',
      infeasible: '\u26a0 高低差に対してルートが短すぎます',
      length: '延長', travel_time: '所要時間',
      construction_cost: '建設費', min_speed: '最低速度',
      submit_run: '本日の記録を送信', handle_ph: 'ハンドルネーム', submit_score: 'スコアを送信',
      survey_title: 'デスクトップ版アンケート',
      survey_intro: 'より大規模なスタンドアロン版を検討中です。どんな要素が欲しいですか？',
      opt_competitors: '競合する道路', opt_topography: '実世界の地形',
      opt_scenarios: 'シナリオ', opt_multiplayer: 'マルチプレイ', opt_regions: 'より広い地域',
      other_ph: 'その他\u2026',
      get_notified: 'リリース時にお知らせします！',
      email_ph: 'メールアドレス（任意・リリース時に1通のみ）',
      send_answers: '回答を送信',
      leaderboard_title: '本日のリーダーボード', plot: 'グラフ',
      plot_hint: '散布図で表示', loading: '読み込み中\u2026',
      controls: '操作', reset: 'リセット', recenter: '中央に戻す',
      add_segment: '+ 区間', remove_segment: '\u2212 区間',
      cars: '車', on: 'ON', off: 'OFF', btn_cars: '車: ON',
      debug: 'デバッグ', btn_debug: 'デバッグ: 勾配',
      instructions: '遊び方',
      instr_intro: 'コストと時間を最小にする道路で2つの町を結びましょう。今日のパズルに勝つには、以前に提案されたルートより {t}% 安く造る必要があります。',
      instr_1: '制御点から伸びる緑のハンドルをドラッグして道路を曲げます',
      instr_2: '制御点をドラッグして移動',
      instr_3: '道路をクリックして制御点を追加',
      instr_4: '制御点を右クリックして削除',
      instr_5: 'スクロールでズーム \u00b7 空白部分をドラッグで移動',
      instr_6: '縦断図をダブルクリックしてその位置をズーム・移動',
      instr_7: '橋梁は高さに関わらず 1,500万ドル/km です。',
      instr_8: 'トンネルは深さに関わらず 2,500万ドル/km です。',
      instr_9: '開削のコストは深さに比例して増加し、トンネルと同額になるまで上がります。',
      scale: '縮尺 1:50,000',
      plot_modal_title: '本日の投稿 \u00b7 コスト \u00d7 時間',
      close_esc: '閉じる (Esc)',
      plegend_current: '現在の道路', plegend_sub: '投稿',
      plegend_pareto: 'パレートフロンティア', plegend_you: 'あなたの投稿',
      err_tick: '1つ以上選択するか、自由記入してください。',
      err_email: 'メールアドレスが正しくないようです。',
      sending: '送信中\u2026',
      thanks_email: 'ありがとうございます。リリース時に1通だけお送りします。',
      thanks: 'ありがとうございます。',
      err_rate: '試行回数が多すぎます \u2014 1時間ほどお待ちください。',
      err_save: '保存できませんでした。少し待ってから再度お試しください。',
      err_network: '通信エラーです。少し待ってから再度お試しください。',
      cp_label: '制御点', cp_sandbox: 'サンドボックス',
      cp_sandbox_hint: '制御点は無制限。サンドボックスの記録はランキング対象外です。',
      cp_no_submit: 'サンドボックス \u2014 本日のランキング対象外です。',
      cp_over_limit: '{k} 個超過。制御点を減らせばランキング対象になります。',
      instr_10: 'ランキングは制御点 {n} 個までです \u2014 サンドボックスで解除できます。',
      music: '音楽', volume: '音量',
      target: '目標',
      close: '閉じる',
      par_win_title: '目標を突破',
      par_win_body: "あなたの道路は代替ルートより速く、コストも {t}% 低くなっています。",
      par_status_win: "\u30b3\u30b9\u30c8\u30fb\u6642\u9593\u3068\u3082\u306b\u76ee\u6a19\u3092\u4e0a\u56de\u3063\u3066\u3044\u307e\u3059",
      par_status_partial: "\u4e00\u65b9\u306f\u76ee\u6a19\u3092\u4e0a\u56de\u308a\u3001\u3082\u3046\u4e00\u65b9\u306f\u4e0b\u56de\u3063\u3066\u3044\u307e\u3059",
      par_status_behind: "\u30b3\u30b9\u30c8\u30fb\u6642\u9593\u3068\u3082\u306b\u76ee\u6a19\u306b\u5c4a\u3044\u3066\u3044\u307e\u305b\u3093",
      /* Coach note (first-run teaching strip). */
      coach_note: 'ヒント',
      coach_dismiss: '非表示',
      coach_prev: '前のヒント',
      coach_next: '次のヒント',
      coach_drag: '制御点をドラッグして道路を動かすほか、ハンドルをドラッグすると曲線を調整できます。',
      coach_structures: '地形が必要とする場所には、橋梁・開削・トンネルが自動で最適に選ばれます。凡例のとおり、橋梁は黄色、トンネルは黒です。コストへの影響に注目してください。縦断図がそれを見るのに役立ちます。',
      coach_points: '道路をクリックしてドラッグすると制御点を追加できます。制御点を右クリックすると削除できます。ランキングで使えるのは最大 {n} 個の制御点までです。',
      coach_submit: '結果に納得したら、ルートを投稿しましょう。',
      coach_steep: '急勾配すぎて建設できません。この区間は高低差に対して短すぎます。もっと長くしてください。',
      hint_fall_line_snap: "F \u30ad\u30fc\u3092\u62bc\u3059\u3068\u3001\u6700\u5f8c\u306b\u64cd\u4f5c\u3057\u305f\u30b3\u30f3\u30c8\u30ed\u30fc\u30eb\u30dd\u30a4\u30f3\u30c8\u3067\u306e\u9053\u8def\u306e\u5411\u304d\u304c\u6700\u5927\u50be\u659c\u7dda\u306b\u5bfe\u3057\u3066\u5782\u76f4\u306b\u306a\u308a\u307e\u3059\u3002\u30cf\u30f3\u30c9\u30eb\u3092\u30c9\u30e9\u30c3\u30b0\u3059\u308b\u969b\u306b Shift \u30ad\u30fc\u3092\u62bc\u3057\u305f\u307e\u307e\u306b\u3059\u308b\u3068\u3001\u89d2\u5ea6\u3092\u4fdd\u3063\u305f\u307e\u307e\u9577\u3055\u3060\u3051\u3092\u5909\u66f4\u3067\u304d\u307e\u3059\u3002"
    }
  };

  var saved = null;
  try { saved = localStorage.getItem('locale'); } catch (e) {}
  if (saved && !DICT[saved]) saved = null;

  var browser = (navigator.languages || [navigator.language || 'en'])
    .map(function (l) { return String(l).toLowerCase().split('-')[0]; })
    .filter(function (l) { return !!DICT[l]; })[0] || null;

  var locale = saved || browser || 'en';

  /* Full BCP-47 tag for number/date formatting: prefer the browser's own
   * regional variant when it matches the chosen language (de-CH gives
   * 1'234.5; plain de gives 1.234,5), else fall back to the bare code. */
  function numberTag() {
    var match = (navigator.languages || []).filter(function (l) {
      return String(l).toLowerCase().split('-')[0] === locale;
    })[0];
    return match || locale;
  }

  function t(key) {
    return (DICT[locale] && DICT[locale][key]) || DICT.en[key] || key;
  }

  function n(x, maxFrac) {
    try {
      return new Intl.NumberFormat(numberTag(), {
        maximumFractionDigits: maxFrac == null ? 1 : maxFrac
      }).format(x);
    } catch (e) { return String(x); }
  }

  function apply() {
    document.documentElement.lang = locale;
    /* One-shot targets: elements client.js overwrites later (subtitle,
     * leaderboard placeholder). Translated at startup, then released so a
     * runtime language switch never stomps client.js-owned content. */
    document.querySelectorAll('[data-i18n-init]').forEach(function (el) {
      el.textContent = t(el.getAttribute('data-i18n-init'));
      el.removeAttribute('data-i18n-init');
    });
    document.querySelectorAll('[data-i18n]').forEach(function (el) {
      el.textContent = t(el.getAttribute('data-i18n'));
    });
    document.querySelectorAll<HTMLInputElement>('[data-i18n-placeholder]').forEach(function (el) {
      el.placeholder = t(el.getAttribute('data-i18n-placeholder'));
    });
    document.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach(function (el) {
      el.title = t(el.getAttribute('data-i18n-title'));
    });
    document.querySelectorAll('.lang-btn').forEach(function (b) {
      if (b.getAttribute('data-lang') === locale) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
  }

  function setLocale(next) {
    if (!DICT[next] || next === locale) return;
    locale = next;
    try { localStorage.setItem('locale', next); } catch (e) {}
    apply();
    document.dispatchEvent(new CustomEvent('localechange', { detail: { locale: locale } }));
  }

  document.querySelectorAll('.lang-btn').forEach(function (b) {
    b.addEventListener('click', function () { setLocale(b.getAttribute('data-lang')); });
  });

  /* Optional IP fallback: if neither a saved choice nor the browser language
   * matched, let the daily-puzzle response's country code decide. client.js
   * calls this with request.cf.country from the worker; it is a no-op once
   * any stronger signal exists. */
  var COUNTRY_TO_LOCALE = {
    DE: 'de', AT: 'de', LI: 'de',
    FR: 'fr', BE: 'fr', LU: 'fr', MC: 'fr',
    IT: 'it', SM: 'it', VA: 'it',
    ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es',
    VE: 'es', EC: 'es', UY: 'es', PY: 'es', BO: 'es', CR: 'es',
    PA: 'es', GT: 'es', HN: 'es', SV: 'es', NI: 'es', DO: 'es', CU: 'es',
    CN: 'zh', TW: 'zh', HK: 'zh', MO: 'zh', SG: 'zh',
    JP: 'jp'
    /* CH deliberately absent: four language regions, so the browser
     * preference is the only sensible signal there. */
  };
  function fromCountry(cc) {
    if (saved || browser) return;
    var next = COUNTRY_TO_LOCALE[String(cc || '').toUpperCase()];
    if (next) setLocale(next);
  }

  apply();

  return { t: t, n: n, setLocale: setLocale, fromCountry: fromCountry,
           get locale() { return locale; } };
})();
